import type {
  ServiceContainmentNoticeObligation,
  ServiceContainmentReview,
  ServiceContainmentProtectedNote,
} from "@reminder-tracker/backend-contracts";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";

import {
  applyServiceContainment,
  getServiceOperationsCapabilities,
  listActiveServiceNotices,
  previewServiceNotice,
  publishServiceNotice,
  resolveServiceNotice,
  reviewServiceContainment,
  readServiceContainmentProtectedNote,
  saveServiceNoticeDraft,
  listDueServiceContainmentNoticeObligations,
  reviewServiceContainmentNoticeObligation,
} from "../../backend/functions";
import { exactUtcTimestamp, formatAdministrationTime } from "../../ui/administration-time";
import { AdministrationWorkSurface } from "../../ui/administration-surface";

function reference(prefix: "corr" | "cmd"): string {
  if (typeof globalThis.crypto.randomUUID !== "function")
    throw new Error("A secure reference generator is unavailable.");
  return `${prefix}_${globalThis.crypto.randomUUID().replaceAll("-", "")}`;
}

export function ProtectedContainmentAndNotices(): React.JSX.Element {
  const [capabilityCorrelation] = useState(() => reference("corr"));
  const capabilities = useQuery(getServiceOperationsCapabilities, {
    correlationId: capabilityCorrelation,
  });
  const granted = (name: string) =>
    capabilities?.capabilities.some(
      (capability) => capability.capability === name && capability.granted,
    ) ?? false;
  return (
    <>
      {granted("operations:restrict") || granted("operations:security-lock") ? (
        <>
          <ContainmentPanel canLock={granted("operations:security-lock")} />
          <NoticeObligationPanel />
        </>
      ) : null}
      {granted("operations:publish-notice") ? <ServiceNoticePanel /> : null}
    </>
  );
}

export function NoticeObligationPanel(): React.JSX.Element {
  const [correlationId] = useState(() => reference("corr"));
  const obligations = useQuery(listDueServiceContainmentNoticeObligations, { correlationId });
  const decide = useMutation(reviewServiceContainmentNoticeObligation);
  const [busyReference, setBusyReference] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [review, setReview] = useState<{
    obligation: ServiceContainmentNoticeObligation;
    decision: "delayed-for-safety" | "unavailable" | "resolved";
  } | null>(null);
  const summary =
    obligations === undefined
      ? "Checking follow-ups…"
      : obligations.completeness === "incomplete"
        ? "Evidence incomplete"
        : obligations.rows.length === 0
          ? "No decisions due"
          : `${obligations.rows.length} ${obligations.rows.length === 1 ? "decision" : "decisions"} due`;

  const confirmDecision = () => {
    if (review === null) return;
    const { obligation, decision } = review;
    setBusyReference(obligation.reference);
    setStatus(null);
    void decide({
      correlationId: reference("corr"),
      commandKey: reference("cmd"),
      reference: obligation.reference,
      expectedRevision: obligation.revision,
      decision,
    })
      .then(() => {
        setStatus(decisionReceipt(decision));
        setReview(null);
      })
      .catch(() => setStatus("The follow-up changed. Refresh before deciding."))
      .finally(() => setBusyReference(null));
  };

  return (
    <AdministrationWorkSurface
      id="notice-follow-up-title"
      title="Containment notice follow-up"
      purpose="Decide how to record customer-notice follow-up after access is restricted or restored. Customer content and destinations remain private."
      state={{
        label: summary,
        tone:
          obligations === undefined
            ? "neutral"
            : obligations.completeness === "incomplete" || obligations.rows.length > 0
              ? "action"
              : "healthy",
      }}
      consequence="A due decision can delay a necessary customer notice. Recording an outcome never claims that a customer received or read a message."
    >
      {obligations === undefined ? (
        <p aria-live="polite" className="empty-state">
          Checking which follow-up decisions are due…
        </p>
      ) : obligations.rows.length === 0 ? (
        <p className="empty-state">No containment notice decisions need attention.</p>
      ) : (
        <ul className="decision-register">
          {obligations.rows.map((obligation) => (
            <li className="decision-row" key={obligation.reference}>
              <div className="decision-row-copy">
                <span className="decision-state">{obligationStateLabel(obligation.state)}</span>
                <h3>Follow up on a containment notice</h3>
                <p>{obligationReason(obligation.containmentMode)}</p>
                <dl className="decision-facts">
                  <div>
                    <dt>Masked {obligation.target.kind}</dt>
                    <dd className="masked-reference">{obligation.target.reference}</dd>
                  </div>
                  <div>
                    <dt>Review due</dt>
                    <dd>
                      <time dateTime={exactUtcTimestamp(obligation.nextReviewAtEpochMilliseconds)}>
                        {formatAdministrationTime(obligation.nextReviewAtEpochMilliseconds)}
                      </time>
                    </dd>
                  </div>
                </dl>
                <p className="decision-boundary">
                  This records the communication outcome. It does not claim the customer read a
                  message.
                </p>
              </div>
              <div className="decision-actions" aria-label="Follow-up decision">
                <button
                  className="primary-action"
                  type="button"
                  disabled={busyReference !== null}
                  onClick={() => setReview({ obligation, decision: "resolved" })}
                >
                  Record follow-up complete
                </button>
                <button
                  className="secondary-action"
                  type="button"
                  disabled={busyReference !== null}
                  onClick={() => setReview({ obligation, decision: "delayed-for-safety" })}
                >
                  Review tomorrow
                </button>
                <button
                  className="caution-action"
                  type="button"
                  disabled={busyReference !== null}
                  onClick={() => setReview({ obligation, decision: "unavailable" })}
                >
                  Record communication unavailable
                </button>
              </div>
              <details className="audit-details compact-audit-details">
                <summary>
                  <span>
                    <strong>Audit details</strong>
                    <small>Evidence state and exact review time</small>
                  </span>
                  <span className="disclosure-label">Show</span>
                </summary>
                <dl className="audit-register audit-content">
                  <div>
                    <dt>Evidence</dt>
                    <dd>{obligations.completeness === "complete" ? "Complete" : "Incomplete"}</dd>
                  </div>
                  <div>
                    <dt>Record revision</dt>
                    <dd>{obligation.revision}</dd>
                  </div>
                  <div>
                    <dt>Exact review time</dt>
                    <dd>{exactUtcTimestamp(obligation.nextReviewAtEpochMilliseconds)}</dd>
                  </div>
                </dl>
              </details>
            </li>
          ))}
        </ul>
      )}
      {review === null ? null : (
        <section className="decision-review" aria-labelledby="follow-up-review-title">
          <div>
            <h3 id="follow-up-review-title">{decisionLabel(review.decision)}</h3>
            <p>{decisionConsequence(review.decision)}</p>
          </div>
          <div className="decision-actions">
            <button
              className={review.decision === "unavailable" ? "caution-action" : "primary-action"}
              type="button"
              disabled={busyReference !== null}
              onClick={confirmDecision}
            >
              Confirm {decisionLabel(review.decision).toLowerCase()}
            </button>
            <button
              className="secondary-action"
              type="button"
              disabled={busyReference !== null}
              onClick={() => setReview(null)}
            >
              Keep decision open
            </button>
          </div>
        </section>
      )}
      {status === null ? null : <p role="status">{status}</p>}
    </AdministrationWorkSurface>
  );
}

function obligationStateLabel(state: ServiceContainmentNoticeObligation["state"]): string {
  if (state === "delayed-for-safety") return "Review scheduled";
  if (state === "unavailable") return "Communication unavailable";
  if (state === "resolved") return "Follow-up recorded";
  return "Decision due";
}

function obligationReason(mode: ServiceContainmentNoticeObligation["containmentMode"]): string {
  if (mode === "active")
    return "A customer notice decision is still needed after access was restored.";
  if (mode === "locked")
    return "A customer notice decision is still needed after a security lock was applied.";
  return "A customer notice decision is still needed after access was restricted.";
}

function decisionLabel(decision: "delayed-for-safety" | "unavailable" | "resolved"): string {
  if (decision === "delayed-for-safety") return "Review tomorrow";
  if (decision === "unavailable") return "Record communication unavailable";
  return "Record follow-up complete";
}

function decisionConsequence(decision: "delayed-for-safety" | "unavailable" | "resolved"): string {
  if (decision === "delayed-for-safety")
    return "This keeps the decision open and schedules another review in one day.";
  if (decision === "unavailable")
    return "This records that communication could not be completed. No delivery will be implied.";
  return "This closes the obligation without claiming that a customer received or read a message.";
}

function decisionReceipt(decision: "delayed-for-safety" | "unavailable" | "resolved"): string {
  if (decision === "delayed-for-safety")
    return "Follow-up kept open and scheduled for review tomorrow.";
  if (decision === "unavailable")
    return "Communication recorded as unavailable. No delivery was implied.";
  return "Follow-up recorded as complete without claiming that a message was read.";
}

function ContainmentPanel({ canLock }: Readonly<{ canLock: boolean }>): React.JSX.Element {
  const review = useMutation(reviewServiceContainment);
  const apply = useMutation(applyServiceContainment);
  const readProtectedNote = useMutation(readServiceContainmentProtectedNote);
  const [kind, setKind] = useState<"group" | "person">("group");
  const [target, setTarget] = useState("");
  const [desiredMode, setDesiredMode] = useState<"active" | "restricted" | "locked">("restricted");
  const [note, setNote] = useState("");
  const [currentReview, setCurrentReview] = useState<ServiceContainmentReview | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [protectedNote, setProtectedNote] = useState<ServiceContainmentProtectedNote | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <AdministrationWorkSurface
      id="containment-title"
      title="Change service access safely"
      purpose="Find one masked person or group, review the current effect, then confirm one access change."
      state={{
        label: currentReview === null ? "Choose a target" : "Ready to confirm",
        tone: currentReview === null ? "neutral" : "monitor",
      }}
      consequence={containmentConsequence(desiredMode)}
    >
      <fieldset className="decision-stage operations-window">
        <legend>1. Find the protected target</legend>
        <label>
          Scope
          <select value={kind} onChange={(event) => setKind(event.target.value as typeof kind)}>
            <option value="group">Group (RTG)</option>
            <option value="person">Person (RTP)</option>
          </select>
        </label>
        <label>
          Masked reference
          <input value={target} onChange={(event) => setTarget(event.target.value.toUpperCase())} />
        </label>
        <label>
          Intended state
          <select
            value={desiredMode}
            onChange={(event) =>
              setDesiredMode(event.target.value as "active" | "restricted" | "locked")
            }
          >
            <option value="restricted">Restrict changes</option>
            {canLock ? <option value="locked">Security lock</option> : null}
            <option value="active">Restore access</option>
          </select>
        </label>
        <button
          className="secondary-action"
          type="button"
          disabled={busy || target.length < 8}
          onClick={() => {
            setBusy(true);
            setResult(null);
            void review({
              correlationId: reference("corr"),
              target: { kind, reference: target },
              desiredMode,
            })
              .then((reviewed) => {
                setCurrentReview(reviewed);
                setProtectedNote(null);
              })
              .catch(() =>
                setResult(
                  "The current containment effect could not be reviewed. Check the reference and your current authorization, then try again.",
                ),
              )
              .finally(() => setBusy(false));
          }}
        >
          Review current effect
        </button>
      </fieldset>
      {currentReview === null ? null : (
        <section className="decision-review" aria-labelledby="containment-review-title">
          <div>
            <h3 id="containment-review-title">2. Review the access change</h3>
            <p>
              <strong>{containmentModeLabel(currentReview.currentMode)}</strong> will change to{" "}
              <strong>{containmentModeLabel(currentReview.desiredMode)}</strong>.
            </p>
            <p>{containmentConsequence(currentReview.desiredMode)}</p>
          </div>
          <label>
            Protected operational note
            <textarea
              value={note}
              maxLength={500}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
          {currentReview.currentMode === "active" ? null : (
            <button
              className="secondary-action"
              type="button"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void readProtectedNote({
                  correlationId: reference("corr"),
                  target: currentReview.target,
                })
                  .then(setProtectedNote)
                  .catch(() =>
                    setResult("The protected note is unavailable with the current authorization."),
                  )
                  .finally(() => setBusy(false));
              }}
            >
              Read current protected note
            </button>
          )}
          {protectedNote?.note === null ? (
            <p>No retained protected note is available for the current state.</p>
          ) : protectedNote?.note === undefined ? null : (
            <div className="notice">
              <strong>Current protected note</strong>
              <p>{protectedNote.note.body}</p>
              <p>
                Retained until{" "}
                <time dateTime={exactUtcTimestamp(protectedNote.note.expiresAtEpochMilliseconds)}>
                  {formatAdministrationTime(protectedNote.note.expiresAtEpochMilliseconds)}
                </time>
                .
              </p>
            </div>
          )}
          <div className="decision-actions">
            <button
              className={
                currentReview.desiredMode === "locked" ? "caution-action" : "primary-action"
              }
              type="button"
              disabled={busy || note.trim().length < 8}
              onClick={() => {
                setBusy(true);
                void apply({
                  correlationId: reference("corr"),
                  commandKey: reference("cmd"),
                  reviewId: currentReview.reviewId,
                  target: currentReview.target,
                  desiredMode: currentReview.desiredMode,
                  expectedRevision: currentReview.currentRevision,
                  reason:
                    currentReview.desiredMode === "active"
                      ? "issue-resolved"
                      : "incident-containment",
                  note,
                })
                  .then((receipt) => {
                    setResult(
                      `${receipt.mode} at revision ${receipt.appliedRevision}. Customer notice: ${receipt.noticeState}. Provider session action: ${receipt.providerRevocationState}.`,
                    );
                    setCurrentReview(null);
                    setNote("");
                  })
                  .catch(() =>
                    setResult(
                      "The reviewed containment change was not confirmed. Review the current target state before trying again.",
                    ),
                  )
                  .finally(() => setBusy(false));
              }}
            >
              {currentReview.desiredMode === "active"
                ? "Confirm restored access"
                : currentReview.desiredMode === "locked"
                  ? "Confirm security lock"
                  : "Confirm restricted access"}
            </button>
            <button
              className="secondary-action"
              type="button"
              disabled={busy}
              onClick={() => setCurrentReview(null)}
            >
              Keep current access
            </button>
          </div>
          <details className="audit-details compact-audit-details">
            <summary>
              <span>
                <strong>Audit details</strong>
                <small>Bounded effect and current revision</small>
              </span>
              <span className="disclosure-label" aria-hidden="true">
                Show
              </span>
            </summary>
            <dl className="audit-register audit-content">
              <div>
                <dt>Protected reads</dt>
                <dd>{currentReview.effect.protectedReads}</dd>
              </div>
              <div>
                <dt>Consumer changes</dt>
                <dd>{currentReview.effect.consumerWrites}</dd>
              </div>
              <div>
                <dt>New external attempts</dt>
                <dd>{currentReview.effect.newExternalAttempts}</dd>
              </div>
              <div>
                <dt>Current revision</dt>
                <dd>{currentReview.currentRevision}</dd>
              </div>
            </dl>
          </details>
        </section>
      )}
      {result === null ? null : <p role="status">{result}</p>}
    </AdministrationWorkSurface>
  );
}

function containmentModeLabel(mode: "active" | "restricted" | "locked"): string {
  if (mode === "active") return "Normal access";
  if (mode === "locked") return "Security lock";
  return "Restricted changes";
}

function containmentConsequence(mode: "active" | "restricted" | "locked"): string {
  if (mode === "active")
    return "Restoring access allows authorised activity to resume; it does not resend earlier reminders.";
  if (mode === "locked")
    return "A security lock blocks protected reads and changes and may revoke provider sessions.";
  return "Restricting access pauses customer changes while the incident is reviewed.";
}

export function ServiceNoticePanel(): React.JSX.Element {
  const [activeCorrelationId] = useState(() => reference("corr"));
  const activeNotices = useQuery(listActiveServiceNotices, {
    correlationId: activeCorrelationId,
  });
  const save = useMutation(saveServiceNoticeDraft);
  const publish = useMutation(publishServiceNotice);
  const resolve = useMutation(resolveServiceNotice);
  const [previewRequest, setPreviewRequest] = useState<{
    correlationId: string;
    incidentId: string;
    revision: number;
  } | null>(null);
  const preview = useQuery(previewServiceNotice, previewRequest ?? "skip");
  const [service, setService] = useState<"email" | "android" | "in-app">("email");
  const [kind, setKind] = useState<"confirmed-disruption" | "planned-maintenance">(
    "confirmed-disruption",
  );
  const [scheduledStart, setScheduledStart] = useState("");
  const [scheduledEnd, setScheduledEnd] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const activeNotice = activeNotices?.notices.find((notice) => notice.service === service) ?? null;
  return (
    <AdministrationWorkSurface
      id="service-notice-title"
      title="Customer service notice"
      purpose="Compose a notice from an approved template, review the exact message, then publish or resolve it deliberately."
      state={{
        label:
          activeNotices === undefined
            ? "Checking active notices…"
            : activeNotices.notices.length === 0
              ? "No active notices"
              : `${activeNotices.notices.length} active ${activeNotices.notices.length === 1 ? "notice" : "notices"}`,
        tone:
          activeNotices === undefined
            ? "neutral"
            : activeNotices.notices.length === 0
              ? "healthy"
              : "monitor",
      }}
      consequence="Publishing changes the message customers can see. Resolving removes the active disruption claim; neither action proves that a customer read it."
    >
      <fieldset className="decision-stage operations-window">
        <legend>1. Compose the notice</legend>
        <label>
          Service
          <select
            value={service}
            onChange={(event) => setService(event.target.value as typeof service)}
          >
            <option value="email">Email</option>
            <option value="android">Android</option>
            <option value="in-app">In-app</option>
          </select>
        </label>
        <label>
          Notice type
          <select value={kind} onChange={(event) => setKind(event.target.value as typeof kind)}>
            <option value="confirmed-disruption">Confirmed disruption</option>
            <option value="planned-maintenance">Planned maintenance</option>
          </select>
        </label>
        {kind === "planned-maintenance" ? (
          <>
            <label>
              Starts (your local time)
              <input
                type="datetime-local"
                value={scheduledStart}
                onChange={(event) => setScheduledStart(event.target.value)}
              />
            </label>
            <label>
              Ends (your local time)
              <input
                type="datetime-local"
                value={scheduledEnd}
                onChange={(event) => setScheduledEnd(event.target.value)}
              />
            </label>
          </>
        ) : null}
        <button
          className="secondary-action"
          type="button"
          disabled={
            busy ||
            (kind === "planned-maintenance" &&
              (scheduledStart === "" ||
                scheduledEnd === "" ||
                !Number.isFinite(Date.parse(scheduledStart)) ||
                !Number.isFinite(Date.parse(scheduledEnd)) ||
                Date.parse(scheduledStart) >= Date.parse(scheduledEnd)))
          }
          onClick={() => {
            setBusy(true);
            setStatus(null);
            void save({
              correlationId: reference("corr"),
              commandKey: reference("cmd"),
              incidentId: null,
              expectedRevision: null,
              replacesIncidentId: activeNotice?.incidentId ?? null,
              replacesExpectedRevision: activeNotice?.revision ?? null,
              service,
              kind,
              alternative: service === "in-app" ? "check-status-or-contact-help" : "check-in-app",
              scheduledStartAtEpochMilliseconds:
                kind === "planned-maintenance" ? Date.parse(scheduledStart) : null,
              scheduledEndAtEpochMilliseconds:
                kind === "planned-maintenance" ? Date.parse(scheduledEnd) : null,
            })
              .then((saved) => {
                setPreviewRequest({
                  correlationId: reference("corr"),
                  incidentId: saved.incidentId,
                  revision: saved.revision,
                });
              })
              .catch(() =>
                setStatus(
                  "The notice draft was not saved. Check the maintenance window and your current authorization, then try again.",
                ),
              )
              .finally(() => setBusy(false));
          }}
        >
          {activeNotice !== null
            ? "Save and preview replacement notice"
            : "Save and preview notice"}
        </button>
      </fieldset>
      {kind === "planned-maintenance" ? (
        <p className="notice">
          The reviewed preview shows the maintenance window in the administrator&apos;s local time
          before publication. Exact UTC evidence remains available in the publication record.
        </p>
      ) : null}
      {preview === undefined ? null : (
        <section className="decision-review" aria-labelledby="service-notice-preview-title">
          <div>
            <h3 id="service-notice-preview-title">2. Review the customer message</h3>
            <h4>{preview.title}</h4>
            <p>{preview.body}</p>
            <p>{preview.action}</p>
            <a href={preview.statusUrl}>Configured service status</a>
          </div>
          <button
            className="caution-action"
            type="button"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void publish({
                correlationId: reference("corr"),
                commandKey: reference("cmd"),
                incidentId: preview.incidentId,
                expectedRevision: preview.revision,
                previewDigest: preview.previewDigest,
              })
                .then(() => {
                  setPreviewRequest(null);
                  setStatus("Notice published from the reviewed preview.");
                })
                .catch(() =>
                  setStatus(
                    "The notice was not published. Refresh and review the latest saved preview before trying again.",
                  ),
                )
                .finally(() => setBusy(false));
            }}
          >
            Publish reviewed notice
          </button>
        </section>
      )}
      {activeNotices === undefined ? (
        <p aria-live="polite">Checking active service notices…</p>
      ) : (
        activeNotices.notices.map((notice) => (
          <section className="decision-row" key={notice.incidentId}>
            <p>
              Active {notice.service} notice · revision {notice.revision}
            </p>
            <button
              className="caution-action"
              type="button"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void resolve({
                  correlationId: reference("corr"),
                  commandKey: reference("cmd"),
                  incidentId: notice.incidentId,
                  expectedRevision: notice.revision,
                })
                  .then(() => {
                    setStatus(
                      "Notice resolved. Consumer banners no longer claim an active disruption.",
                    );
                  })
                  .catch(() =>
                    setStatus(
                      "Resolution was not confirmed. Refresh the incident before trying again.",
                    ),
                  )
                  .finally(() => setBusy(false));
              }}
            >
              Resolve active notice
            </button>
          </section>
        ))
      )}
      {status === null ? null : <p role="status">{status}</p>}
    </AdministrationWorkSurface>
  );
}
